import { Canvas, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { motion, AnimatePresence } from "framer-motion";
import { X, Move3d, Maximize2 } from "lucide-react";

/**
 * Inside-out sphere with equirectangular texture.
 * Rotates via mouse/touch drag. No drei needed.
 */
function PanoSphere({ image }) {
    const texture = useMemo(() => {
        const loader = new THREE.TextureLoader();
        loader.crossOrigin = "anonymous";
        const t = loader.load(image);
        t.colorSpace = THREE.SRGBColorSpace;
        return t;
    }, [image]);

    // clean up
    useEffect(() => () => texture.dispose(), [texture]);

    return (
        <mesh scale={[-1, 1, 1]}>
            <sphereGeometry args={[500, 60, 40]} />
            <meshBasicMaterial map={texture} side={THREE.BackSide} />
        </mesh>
    );
}

/**
 * Custom drag-to-rotate camera controller (no drei OrbitControls).
 */
function DragController({ autoRotate = true }) {
    const { camera, gl } = useThree();
    const state = useRef({
        lon: 0,
        lat: 0,
        dragging: false,
        lastX: 0,
        lastY: 0,
    });

    useEffect(() => {
        const dom = gl.domElement;

        const onDown = (e) => {
            state.current.dragging = true;
            const p = "touches" in e ? e.touches[0] : e;
            state.current.lastX = p.clientX;
            state.current.lastY = p.clientY;
        };
        const onMove = (e) => {
            if (!state.current.dragging) return;
            const p = "touches" in e ? e.touches[0] : e;
            const dx = p.clientX - state.current.lastX;
            const dy = p.clientY - state.current.lastY;
            state.current.lastX = p.clientX;
            state.current.lastY = p.clientY;
            state.current.lon -= dx * 0.18;
            state.current.lat = Math.max(-85, Math.min(85, state.current.lat + dy * 0.18));
        };
        const onUp = () => {
            state.current.dragging = false;
        };

        dom.addEventListener("mousedown", onDown);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        dom.addEventListener("touchstart", onDown, { passive: true });
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("touchend", onUp);

        let raf;
        const tick = () => {
            if (autoRotate && !state.current.dragging) state.current.lon += 0.03;
            const phi = THREE.MathUtils.degToRad(90 - state.current.lat);
            const theta = THREE.MathUtils.degToRad(state.current.lon);
            const target = new THREE.Vector3(
                Math.sin(phi) * Math.cos(theta),
                Math.cos(phi),
                Math.sin(phi) * Math.sin(theta)
            );
            camera.lookAt(target);
            raf = requestAnimationFrame(tick);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            dom.removeEventListener("mousedown", onDown);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            dom.removeEventListener("touchstart", onDown);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onUp);
        };
    }, [camera, gl, autoRotate]);

    return null;
}

function ViewerCanvas({ image }) {
    return (
        <Canvas
            gl={{ antialias: true }}
            dpr={[1, 1.5]}
            camera={{ position: [0, 0, 0.001], fov: 72 }}
            onCreated={({ gl }) => {
                gl.setClearColor(0x062b1a);
            }}
        >
            <PanoSphere image={image} />
            <DragController autoRotate />
        </Canvas>
    );
}

/**
 * Modal 360° tour viewer.
 * Opens full-screen with an equirectangular panorama.
 */
export default function Project360Viewer({ image, projectName }) {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            {/* Trigger card */}
            <button
                data-testid="open-360-viewer"
                onClick={() => setOpen(true)}
                className="group relative w-full overflow-hidden bg-forest-800 border border-lime/25 hover:border-lime transition-colors"
            >
                <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-6 p-6 md:p-8 text-left">
                    <div className="md:col-span-2">
                        <div className="relative h-16 w-16 flex items-center justify-center bg-lime text-forest-900 rounded-full overflow-hidden">
                            <Move3d size={22} strokeWidth={1.5} />
                            <span className="absolute inset-0 rounded-full border-2 border-lime animate-ping opacity-50" />
                        </div>
                    </div>
                    <div className="md:col-span-8">
                        <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-2">
                            Immersive 360° tour · Interactive
                        </div>
                        <h4 className="font-serif-display text-2xl md:text-3xl leading-tight text-ivory group-hover:text-lime transition-colors">
                            Walk through {projectName}
                        </h4>
                        <p className="mt-2 text-ivory/60 text-sm">
                            Drag anywhere to look around. Best experienced full-screen.
                        </p>
                    </div>
                    <div className="md:col-span-2 md:justify-self-end">
                        <span className="inline-flex items-center gap-2 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory group-hover:text-lime transition-colors">
                            Launch <Maximize2 size={13} />
                        </span>
                    </div>
                </div>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="viewer"
                        data-testid="viewer-360-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-[110] bg-forest-950"
                    >
                        <ViewerCanvas image={image} />

                        {/* Top bar */}
                        <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-6 md:px-10 py-5 md:py-6 bg-gradient-to-b from-forest-950/85 to-transparent">
                            <div>
                                <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mb-1">
                                    360° Tour · Interactive
                                </div>
                                <div className="font-serif-display text-2xl md:text-3xl text-ivory leading-tight">
                                    {projectName}
                                </div>
                            </div>
                            <button
                                data-testid="close-360-viewer"
                                onClick={() => setOpen(false)}
                                className="h-12 w-12 border border-ivory/25 text-ivory hover:border-lime hover:text-lime transition-colors flex items-center justify-center"
                                aria-label="Close 360 viewer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Hint bottom */}
                        <div className="absolute bottom-0 inset-x-0 z-10 px-6 md:px-10 py-5 md:py-6 bg-gradient-to-t from-forest-950/85 to-transparent flex items-center justify-between">
                            <div className="flex items-center gap-3 text-ivory/80">
                                <Move3d size={16} className="text-lime animate-pulse" />
                                <span className="font-mono-mini text-[10px] tracking-[0.32em] uppercase">
                                    Drag to look around
                                </span>
                            </div>
                            <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/50 hidden md:block">
                                Auto-rotate: On
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
